import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, AttributionControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import '../address/mapIconFix';
import { useTranslation } from 'react-i18next';

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors';
const DEFAULT_CENTER = [48.0, 15.0];

/** Значок места: одна школа — обычная метка, несколько — круг с числом; приблизительное место (центр города) — серый круг «≈». */
function makeIcon(place) {
    const count = place.schools.length;
    if (!place.approximate && count === 1) return undefined;
    const bg = place.approximate ? '#94a3b8' : '#EB3333';
    const border = place.approximate ? '3px dashed #fff' : '3px solid #fff';
    return L.divIcon({
        className: '',
        html:
            `<div style="width:36px;height:36px;border-radius:50%;background:${bg};color:#fff;border:${border};` +
            'box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;' +
            `font:700 14px 'Segoe UI',Arial,sans-serif">${place.approximate ? '≈' : ''}${count}</div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
        popupAnchor: [0, -18],
    });
}

/** Содержимое всплывающего окна как DOM-элемент: имена школ вставляются как текст, не как HTML. */
function buildPopup(place, t, onSelect) {
    const root = document.createElement('div');
    root.style.cssText = "min-width:220px;max-width:280px;font-family:'Segoe UI',Arial,sans-serif";

    const title = document.createElement('div');
    title.style.cssText = 'font-weight:700;margin-bottom:4px';
    title.textContent = place.approximate
        ? t('placesMap:approxTitle', { city: place.city })
        : (place.name || place.addressFull || place.city);
    root.appendChild(title);

    const sub = document.createElement('div');
    sub.style.cssText = 'font-size:12px;color:#64748b;margin-bottom:8px';
    sub.textContent = place.approximate
        ? t('placesMap:approxNote')
        : (place.schools.length > 1 ? t('placesMap:schoolsHere', { count: place.schools.length }) : '');
    if (sub.textContent) root.appendChild(sub);

    const list = document.createElement('div');
    list.style.cssText = 'max-height:220px;overflow-y:auto';
    place.schools.forEach((school) => {
        const row = document.createElement('div');
        row.style.cssText = 'padding:6px 0;border-top:1px solid #f1f5f9';

        const name = document.createElement('div');
        name.style.cssText = 'font-weight:600;font-size:13px';
        name.textContent = `${school.sportIcon ? `${school.sportIcon} ` : ''}${school.name}`;
        row.appendChild(name);

        const meta = document.createElement('div');
        meta.style.cssText = 'font-size:11px;color:#64748b;margin:2px 0 6px';
        meta.textContent = [school.sportType, school.kind === 'room' ? t('placesMap:room') : ''].filter(Boolean).join(' · ');
        row.appendChild(meta);

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = t('placesMap:select');
        btn.style.cssText =
            'padding:5px 10px;border:none;border-radius:8px;background:#EB3333;color:#fff;font-weight:600;font-size:12px;cursor:pointer;width:100%';
        btn.addEventListener('click', () => onSelect?.(school));
        row.appendChild(btn);

        list.appendChild(row);
    });
    root.appendChild(list);
    return root;
}

function PlaceLayer({ places, selectedSchoolId, onSelectSchool }) {
    const map = useMap();
    const { t } = useTranslation(['placesMap']);
    const groupRef = useRef(null);
    const markersRef = useRef(new Map());
    // Колбэки держим в ref: иначе слой перестраивался бы на каждый рендер родителя, а карта «прыгала» бы.
    const selectRef = useRef(onSelectSchool);
    selectRef.current = onSelectSchool;
    const tRef = useRef(t);
    tRef.current = t;

    useEffect(() => {
        const group = L.markerClusterGroup({
            showCoverageOnHover: false,
            maxClusterRadius: 48,
            spiderfyOnMaxZoom: true,
        });
        markersRef.current = new Map();

        places.forEach((place) => {
            const icon = makeIcon(place);
            const marker = L.marker([place.lat, place.lng], icon ? { icon } : undefined);
            marker.bindPopup(() => buildPopup(place, tRef.current, (s) => selectRef.current?.(s)), { minWidth: 220, maxWidth: 300 });
            group.addLayer(marker);
            markersRef.current.set(place.id, marker);
        });

        map.addLayer(group);
        groupRef.current = group;

        if (places.length === 1) {
            map.setView([places[0].lat, places[0].lng], 15, { animate: false });
        } else if (places.length > 1) {
            map.fitBounds(group.getBounds(), { padding: [48, 48], maxZoom: 14, animate: false });
        }

        return () => {
            map.removeLayer(group);
            groupRef.current = null;
        };
    }, [places, map]);

    useEffect(() => {
        if (selectedSchoolId == null || !groupRef.current) return;
        const place = places.find((p) => p.schools.some((s) => s.id === selectedSchoolId));
        const marker = place && markersRef.current.get(place.id);
        if (!marker) return;
        groupRef.current.zoomToShowLayer(marker, () => marker.openPopup());
    }, [selectedSchoolId, places]);

    return null;
}

/**
 * Карта поиска школ по местам: одна метка на здание, под ней список школ и секций.
 * Рядом стоящие здания собираются в кластеры. Школы без точного адреса показаны серым кругом в центре города.
 */
export default function PlacesMap({ places = [], selectedSchool = null, onSelectSchool, height = '420px' }) {
    const { t } = useTranslation(['placesMap']);

    if (places.length === 0) {
        return (
            <div style={{
                height, borderRadius: 12, border: '1px dashed #cbd5e1', background: '#f8fafc', display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 14, textAlign: 'center', padding: 24,
            }}>
                {t('placesMap:noPlaces')}
            </div>
        );
    }

    return (
        <div style={{
            height, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--sp-gray-200)', position: 'relative', zIndex: 0,
        }}>
            <MapContainer
                center={DEFAULT_CENTER}
                zoom={5}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom
                attributionControl={false}
            >
                <TileLayer url={TILE_URL} attribution={ATTRIBUTION} maxZoom={19} />
                <AttributionControl prefix={false} />
                <PlaceLayer places={places} selectedSchoolId={selectedSchool?.id ?? null} onSelectSchool={onSelectSchool} />
            </MapContainer>
        </div>
    );
}
